import { inject, Service } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { NavigationEnd, Router } from "@angular/router";

import { AuthenticationService } from "@/shared-module/services/authentication.service";
import { UrlService } from "@/shared-module/services/url.service";

/** Longest dwell we are willing to attribute to a single page. */
const MAX_DWELL_MS = 30 * 60 * 1000;

/** Must stay well under the server's SessionIdleTimeoutMinutes. */
const HEARTBEAT_MS = 60 * 1000;

/**
 * A gap longer than this means the previous session is over as far as the
 * server's sweeper is concerned, so we start a new one rather than resuming
 * a session it has already closed. Mirrors SessionIdleTimeoutMinutes.
 */
const SESSION_IDLE_MS = 15 * 60 * 1000;

const LAST_ACTIVITY_KEY = "lastActivityAt";

/**
 * Page-view and session telemetry.
 *
 * Replaces the inline NavigationEnd block that used to live in AppComponent.
 * Three things it adds over that version:
 *  - the page is described (route, page/path/sub-path, title), not just a URL;
 *  - "active" time is counted only while the tab is visible, so a page left
 *    open in a background tab no longer looks like half an hour of reading;
 *  - the last page of a session is actually recorded, via a pagehide beacon.
 *    The navigation-driven flush can never catch it, because no navigation
 *    follows.
 */
@Service()
export class ActivityService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private authenticationService = inject(AuthenticationService);
  private urlService = inject(UrlService);

  private started = false;

  private currentUrl: string | null = null;
  private currentTitle: string | null = null;
  private enteredAt: number | null = null;

  /** Visible-only time on the current page. */
  private activeMs = 0;
  private visibleSince: number | null = null;

  /** Visible-only time not yet reported by a heartbeat. */
  private unreportedActiveMs = 0;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;

  start(): void {
    if (this.started) return;
    this.started = true;

    this.resumeOrStartSession();

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) this.onNavigated(event.urlAfterRedirects);
    });

    document.addEventListener("visibilitychange", () => this.onVisibilityChange());
    // pagehide fires on tab close, navigation away and bfcache entry, where
    // unload is unreliable; this is the only chance to report the last page.
    window.addEventListener("pagehide", () => this.onPageHide());

    if (document.visibilityState === "visible") this.visibleSince = Date.now();
    this.startHeartbeat();
  }

  /**
   * A new session id per login, and per resume after a long gap. Without the
   * second rule the id in localStorage would live until the next login, so a
   * "session" meant "since the last login on this browser" and never ended.
   */
  private resumeOrStartSession(): void {
    const last = Number(localStorage.getItem(LAST_ACTIVITY_KEY) ?? 0);
    if (!localStorage.getItem("sessionId") || Date.now() - last > SESSION_IDLE_MS) {
      localStorage.setItem("sessionId", crypto.randomUUID());
    }
    this.markActivity();
  }

  private markActivity(): void {
    try {
      localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
    } catch {
      // Private mode / blocked storage: session continuity degrades, nothing breaks.
    }
  }

  private onNavigated(url: string): void {
    this.flush("Navigate");

    this.currentUrl = url;
    this.enteredAt = Date.now();
    this.activeMs = 0;
    this.visibleSince = document.visibilityState === "visible" ? Date.now() : null;

    // The title is set by the TitleStrategy during navigation; read it after
    // this turn of the event loop so we capture the new page's title, not the
    // one we have just left.
    this.currentTitle = null;
    setTimeout(() => (this.currentTitle = this.cleanTitle(document.title)), 0);

    this.markActivity();
  }

  private onVisibilityChange(): void {
    if (document.visibilityState === "visible") {
      this.visibleSince = Date.now();
      this.markActivity();
    } else {
      this.accumulateActive();
    }
  }

  private accumulateActive(): void {
    if (this.visibleSince === null) return;
    const delta = Date.now() - this.visibleSince;
    this.activeMs += delta;
    this.unreportedActiveMs += delta;
    this.visibleSince = null;
  }

  private startHeartbeat(): void {
    this.heartbeatTimer = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      if (!this.authenticationService.currentUserValue) return;

      // Fold the time since the last beat into the reported total without
      // ending the current visible stretch.
      const now = Date.now();
      if (this.visibleSince !== null) {
        const delta = now - this.visibleSince;
        this.activeMs += delta;
        this.unreportedActiveMs += delta;
        this.visibleSince = now;
      }

      const activeMs = this.unreportedActiveMs;
      this.unreportedActiveMs = 0;
      this.markActivity();

      this.http
        .post("api/session/heartbeat", { activeMs })
        .subscribe({ error: () => {} });
    }, HEARTBEAT_MS);
  }

  /** Posts the page just left. Only attributable while logged in. */
  private flush(endReason: string): void {
    if (this.currentUrl === null || this.enteredAt === null) return;
    if (!this.authenticationService.currentUserValue) return;

    const payload = this.buildPageView(endReason);
    this.currentUrl = null;
    this.enteredAt = null;

    this.http.post("api/pageview", payload).subscribe({ error: () => {} });
  }

  private onPageHide(): void {
    const user = this.authenticationService.currentUserValue;
    if (!user?.token) return;

    this.accumulateActive();
    const finalPageView =
      this.currentUrl !== null && this.enteredAt !== null
        ? this.buildPageView("Unload")
        : null;

    // sendBeacon cannot set an Authorization header, so the token travels in
    // the body and the server validates it there.
    const body = JSON.stringify({
      token: user.token,
      sessionId: localStorage.getItem("sessionId"),
      reason: "TabClosed",
      finalPageView,
    });

    try {
      navigator.sendBeacon(
        "api/session/end",
        new Blob([body], { type: "application/json" }),
      );
    } catch {
      // Nothing useful to do while the page is going away; the server-side
      // sweeper closes the session either way.
    }
  }

  private buildPageView(endReason: string) {
    const now = Date.now();
    const parsed = this.urlService.parseUrl(this.currentUrl, null, true);

    return {
      url: this.currentUrl,
      durationMs: Math.min(now - this.enteredAt, MAX_DWELL_MS),
      activeMs: Math.min(this.activeMs, MAX_DWELL_MS),
      pageId: parsed.page ?? null,
      path: parsed.path || null,
      subPath: parsed.subpath || null,
      title: this.currentTitle,
      endReason,
    };
  }

  /** The TitleStrategy renders "<page> | MegaLearning". */
  private cleanTitle(title: string): string | null {
    if (!title) return null;
    const separator = title.indexOf(" | ");
    return (separator > 0 ? title.substring(0, separator) : title).trim() || null;
  }
}
